import {
    IPublicPendingTaskNode,
    IPublicPendingTaskPollingResponse,
    PublicPendingTaskStatus
} from '~/types/server.types';
import { computed, observable } from 'mobx';
import { trackError } from '~/services/logger/error.utils';
import { CXRootStore } from '~/components/ClientExperience/stores/cx-root.store';
import { logInfo } from '~/services/logger/logger.utils';
import dayjs from '~/common/dayjs';

export class PublicPendingTaskController {
    rootStore: CXRootStore;
    pollingInterval: number = 500;
    maxPollCount = 300;
    @observable executingTasksHash: Record<string, IPublicPendingTaskNode> = {};
    @observable pollingTimeoutId: NodeJS.Timeout = null;

    constructor(rootStore: CXRootStore) {
        this.rootStore = rootStore;
    }

    @computed
    private get taskIds() {
        return Object.keys(this.executingTasksHash);
    }

    @computed
    private get hasPendingTasks() {
        return !!this.taskIds.length;
    }

    @computed
    private get isPollingOn() {
        return !!this.pollingTimeoutId;
    }

    public onLogout() {
        this.cleanupTasks();
        this.cancelPolling();
    }

    private cleanupTasks() {
        Object.values(this.executingTasksHash).forEach(taskNode => {
            this.cancelPendingTask(taskNode);
        });
        this.executingTasksHash = {};
    }

    private cancelPolling() {
        if (this.pollingTimeoutId) {
            clearTimeout(this.pollingTimeoutId);
        }
        this.pollingTimeoutId = null;
    }

    private createTaskNode({
        taskData,
        taskId,
        fetchResultsHook
    }: {
        taskData: any;
        taskId?: string;
        fetchResultsHook: (token: string) => any;
    }) {
        let reject;
        let resolve;
        const promise = new Promise((res, rej) => {
            resolve = res;
            reject = rej;
        });
        const taskNode: IPublicPendingTaskNode = {
            canceled: false,
            pollCount: 0,
            promise,
            reject,
            resolve,
            taskData,
            taskId,
            fetchResultsHook
        };
        return taskNode;
    }

    public cancelTaskPolling(taskPromise: IPublicPendingTaskNode['promise']) {
        const taskNodeToCancel = Object.values(this.executingTasksHash).find(
            task => task.promise === taskPromise
        );
        if (taskNodeToCancel) {
            this.cancelPendingTask(taskNodeToCancel);
        } else {
            trackError({
                error: new Error('no taskNode to cancel'),
                message:
                    'could not find the task promise to cancel. could it be that you saved the wrong promise?'
            });
        }
    }

    public async createPublicPendingTask(
        taskData: Record<string, any>,
        fetchResultsHook: (token: string) => any
    ): Promise<unknown> {
        const taskNode = this.createTaskNode({ taskData, fetchResultsHook });
        try {
            taskNode.taskId = taskData.taskId;
            this.registerPendingTask(taskNode);
        } catch (error) {
            trackError({ error });
            taskNode.reject(error);
        }
        return taskNode.promise;
    }

    private registerPendingTask(taskNode: IPublicPendingTaskNode) {
        this.executingTasksHash[taskNode.taskId] = taskNode;
        this.initiatePolling();
    }

    private initiatePolling() {
        if (!this.isPollingOn && this.hasPendingTasks) {
            this.pollingTimeoutId = setTimeout(
                this.pendingTasksTimeoutHandler.bind(this),
                0
            );
        }
    }

    private async pendingTasksTimeoutHandler() {
        let lastTaskId;
        let currentLogTime;
        if (this.hasPendingTasks) {
            const { publicPendingTaskService } = this.rootStore;
            for (const taskId of this.taskIds) {
                lastTaskId = taskId;
                const taskNode = this.executingTasksHash[taskId];
                try {
                    currentLogTime = this.getLogTime();
                    logInfo(
                        `Puf submit UUID ${taskId} in pending task Handler start`,
                        {
                            currentLogTime,
                            taskNode,
                            event: `Puf submit in pending task Handler`,
                            pollCount: taskNode.pollCount++,
                            uuid: taskId
                        }
                    );

                    let response;
                    const rejPromiseTimeOut = new Promise((_r, rej) =>
                        setTimeout(() => rej('self promise time out'), 1000)
                    );
                    const s3Promise =
                        publicPendingTaskService.fetchPublicPendingTaskData(
                            taskNode.taskData?.url
                        );

                    response = await Promise.race([
                        rejPromiseTimeOut,
                        s3Promise
                    ]);
                    currentLogTime = this.getLogTime();
                    logInfo(
                        `Puf submit UUID ${taskId} getting json from s3 end`,
                        {
                            currentLogTime,
                            response,
                            event: `Got json from s3 end`,
                            pollCount: taskNode.pollCount++,
                            uuid: taskId
                        }
                    );

                    if (taskNode.canceled) {
                        taskNode.reject('canceled');
                        this.removeTaskById(taskNode.taskId);
                        continue;
                    }

                    const isFinished = await this.handlePendingTaskUpdate(
                        taskNode,
                        response
                    );
                    if (
                        !isFinished &&
                        taskNode.pollCount >= this.maxPollCount
                    ) {
                        taskNode.reject('Public pending task timed out');
                        this.cancelPendingTask(taskNode);
                    }
                    taskNode.pollCount++;
                } catch (error) {
                    taskNode.pollCount++;
                    currentLogTime = this.getLogTime();

                    logInfo(`Puf submit UUID ${taskId} in error.`, {
                        currentLogTime,
                        error,
                        errorStatus: error?.status,
                        event: `Puf submit in error`,
                        pollCount: taskNode.pollCount++,
                        uuid: taskId
                    });

                    if (error.status != 404) {
                        trackError({
                            error: error,
                            message:
                                'error fetching token for public task error code not 404'
                        });
                    }
                }
            }
        }

        if (this.hasPendingTasks) {
            currentLogTime = this.getLogTime();
            logInfo(
                `Puf submit lastTaskId: ${lastTaskId} calling pendingTasksTimeoutHandler with pollingInterval: ${this.pollingInterval}`,
                {
                    currentLogTime,
                    event: `has pending tasks`,
                    lastTaskId
                }
            );
            this.pollingTimeoutId = setTimeout(() => {
                currentLogTime = this.getLogTime();
                logInfo(
                    `Puf submit lastTaskId ${lastTaskId} setTimeOut calling pendingTasksTimeoutHandler.`,
                    {
                        currentLogTime,
                        event: `After Interval time`,
                        lastTaskId
                    }
                );
                this.pendingTasksTimeoutHandler.bind(this)();
            }, this.pollingInterval);
        } else {
            this.pollingTimeoutId = null;
        }

        currentLogTime = this.getLogTime();
        logInfo(
            `Puf submit lastTaskId ${lastTaskId} in pending task Handler end`,
            {
                currentLogTime,
                event: `Pending task Handler end`,
                pollingInterval: this.pollingInterval,
                lastTaskId
            }
        );
    }

    private getLogTime() {
        return dayjs().format('MMM DD YYYY HH:mm:ss');
    }

    private removeTaskById(taskId: string) {
        delete this.executingTasksHash[taskId];
    }

    private async handlePendingTaskUpdate(
        taskNode,
        { token, status, error }: IPublicPendingTaskPollingResponse
    ) {
        if (!taskNode) {
            return;
        }

        // if the task is not finished
        // and the task is not failed
        if ((!status || !token) && !error) {
            return;
        }
        if (status === PublicPendingTaskStatus.Pending) {
            return;
        }
        if (status === PublicPendingTaskStatus.Finished) {
            const resp = await taskNode.fetchResultsHook(token);
            taskNode.resolve(resp);
            this.removeTaskById(taskNode.taskId);
            return true;
        }
        taskNode.reject(error);
        this.cancelPendingTask(taskNode);
    }

    private cancelPendingTask(task: IPublicPendingTaskNode) {
        task.canceled = true;
        this.removeTaskById(task.taskId);
    }
}
